import { Sprintplanning2ViewComponent } from './sprintplanning2-view.component';
import { SynchronizationService } from '../../services/synchronization.service';
import { ComponentFactoryResolver, ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import { UserstoryService } from '../../services/userstory.service';
import { SettingsService } from '../../services/settings.service';
import { IssuecardComponent } from '../../components/issuecard/issuecard.component';
import { HTMLElementPositioningHelper } from '../../util/htmlelement-positioning-helper';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { IssueComponent } from '../../components/issue/issue.component';
import { IssueTypeMappingUtil } from '../../util/issuetype-mapping-util';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Userstory } from '../../models/userstory';
import { Status } from '../../models/status';
import { Statuscategory } from '../../models/statuscategory';
import { Task } from '../../models/task';
import { SyncEventType } from '../../models/enums/syncEventType';
import { EventIssuecardOpened } from '../../models/syncEvents/realtimeEvents/eventIssuecardOpened';
import { EventIssuecardToForeground } from '../../models/syncEvents/realtimeEvents/eventIssuecardToForeground';
import { ActionViewSynchroHelper } from '../actionview-synchro-helper';
import { EventIssueDeleted } from '../../models/syncEvents/persistenceEvents/eventIssueDeleted';
import { EventIssueCreated } from '../../models/syncEvents/persistenceEvents/eventIssueCreated';
import { delay } from 'rxjs';

/**
 * This class is encapsulating the handling of synchronsation-events for the sprintplanning2-component
 */
@Injectable()
export class Sprintplanning2SynchroHelper {
  constructor(
    private syncService: SynchronizationService,
    private actionViewSync: ActionViewSynchroHelper,
    private userStoryService: UserstoryService,
    private settingsService: SettingsService,
    private positionHelper: HTMLElementPositioningHelper,
    private dynamicElementService: DynamicElementService,
    private resolver: ComponentFactoryResolver
  ) { }

  public initSyncEventListeners(sp2Board: Sprintplanning2ViewComponent) {
    // Init events shared by all boards
    this.actionViewSync.initSyncEventListeners(sp2Board);
    // Init events specific to this board
    this.initOpenIssuecardSubscription(sp2Board);
    this.initIssueMoveStartedSubscription(sp2Board);
    this.initIssueMovedSubscription(sp2Board);
    this.initIssueMoveEndedSubscription(sp2Board);
    this.initIssueCardClickedSubscription(sp2Board);
    this.initCreatedNewSubissueSubscription(sp2Board);
    this.initCreatedNewIssueSubscription(sp2Board);
    this.initCreateIssueSubscription(sp2Board);
    this.initCreateSubIssueSubscription(sp2Board);
    this.initDiscardCreateIssueSubscription(sp2Board);
    this.initDiscardCreateSubIssueSubscription(sp2Board);
    this.initUpdateIssueSubscription(sp2Board);
    this.initUpdateSubIssueSubscription(sp2Board);
    this.initDeleteIssueSubscription(sp2Board);
    this.initChangeIssueOrderSubscription(sp2Board);
    this.initChangeTaskOrderSubscription(sp2Board);
    this.initMoveTaskToOtherIssueSubscription(sp2Board);
    this.initMoveIssueToSprintBacklogSubscription(sp2Board);
    this.initMoveIssueToProductBacklogSubscription(sp2Board);
    this.initVisibleColumnsChangeSubscription(sp2Board);
    this.initAddSplitIssueAtIndexSubscription(sp2Board);
  }

  private initOpenIssuecardSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUECARD_OPENED).subscribe(payload => {
      const event = EventIssuecardOpened.fromPayload(payload);
      let issueComponent: IssueComponent = null;

      // New issue is being created
      if (event.isNewIssue && event.isSubissue) {
        issueComponent = sp2Board.createIssueComponent(event.parentIssueId, event.issueId);
      }
      else if (event.isNewIssue && !event.isSubissue) {
        // In SP2 Issue creation is always done through the product backlog
        issueComponent = sp2Board.productBacklog.createIssueComponent(event.issueId);
      }
      // Existing issue is being edited
      else {
        issueComponent = sp2Board.getIssueComponent(event.issueId);
        // If component not found try getting it from from product backlog
        if (!issueComponent) {
          issueComponent = sp2Board.productBacklog.getIssueComponent(event.issueId);
        }
      }
      if (issueComponent) {
        issueComponent.onRemoteCardOpened(event);
      }
    }));
  }

  private initIssueMoveStartedSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('IssueMoveStarted').subscribe(data => {
      // Create new floating IssueComponent
      const insertionPoint: ViewContainerRef = this.dynamicElementService.getInsertionPoint();
      const floatingIssueComponentRef: ComponentRef<IssueComponent> = insertionPoint.createComponent(IssueComponent);
      const issue: IssueComponent = floatingIssueComponentRef.instance;
      if (data.source === 'sp2') {
        issue.issue = sp2Board.getIssueComponent(data.id).issue;
      } else if (data.source === 'backlog') {
        issue.issue = sp2Board.productBacklog.getIssueComponent(data.id).issue;
      }
      issue.projectKey = sp2Board.projectKey.toString();
      issue.componentRef = floatingIssueComponentRef;
      issue.inheritedIssueType = issue.issue.issueType;

      // Set floating component style
      floatingIssueComponentRef.location.nativeElement.classList.add('issue-while-drag', 'invisible');

      // Add new floating IssueComponent to dynamicElementService
      this.dynamicElementService.insert(floatingIssueComponentRef);
    }));
  }

  private initIssueMovedSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('IssueMoved').subscribe(data => {
      // Get ref of the floating issue
      const floatingIssueComponentRef = this.dynamicElementService.getComponentRefForIssue(data.id);

      // Make the floating issue visible
      floatingIssueComponentRef.location.nativeElement.classList.remove('invisible');

      // Set less opacity to the issue to be moved
      let issueComponent;
      if (data.source === 'sp2') {
        issueComponent = sp2Board.getNativeIssueComponent(data.id);
      } else if (data.source === 'backlog') {
        issueComponent = sp2Board.productBacklog.getNativeIssueComponent(data.id);
      }

      if (issueComponent) {
        issueComponent.nativeElement.classList.add('issue-in-drag');
      }

      // Adopt moving position
      this.positionHelper.setRelativePosition(floatingIssueComponentRef.location.nativeElement, data.position.left, data.position.top);
    }));
  }

  private initIssueMoveEndedSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('IssueMoveEnded').subscribe(data => {
      // Get ref of the floating issue
      const floatingIssueComponentRef = this.dynamicElementService.getComponentRefForIssue(data.id);

      // Rest opacity of the moved issue
      let issueComponent;
      if (data.source === 'sp2') {
        issueComponent = sp2Board.getNativeIssueComponent(data.id);
      } else if (data.source === 'backlog') {
        issueComponent = sp2Board.productBacklog.getNativeIssueComponent(data.id);
      }

      if (issueComponent) {
        issueComponent.nativeElement.classList.remove('issue-in-drag');
      }

      // Remove the floating issue from dynamicElementService
      this.dynamicElementService.remove(floatingIssueComponentRef);
    }));
  }

  private initIssueCardClickedSubscription(sp2Board: Sprintplanning2ViewComponent) {
    // eslint-disable-next-line max-len
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUECARD_TO_FOREGROUND).pipe(delay(10)).subscribe(payload => {
      const event = EventIssuecardToForeground.fromPayload(payload);
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (card) {
        this.dynamicElementService.select(card.componentRef);
      } else {
        console.log('initIssueCardClickedSubscription: card not found');
      }
    }));
  }

  private initCreatedNewSubissueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUE_CREATED).subscribe(payload => {
      const event = EventIssueCreated.fromPayload(payload);
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.temporaryIssueId);
      if (card) {
        card.discardChangesAndCloseThisCard();
      } else {
        console.log('initCreatedNewSubissueSubscription: card not found');
      }
      // Load the newly created subtask and add it to the board datamodel
      if (event.isSubissue) {
        this.userStoryService.getSubissue(this.settingsService.getProjectName(), event.issueId).subscribe(newSubtask => {
          // TODO for some reason the task returned by pywall has the wrong userid set, we should fix this but until
          // then we overwrite it here.
          newSubtask.parentIssueId = event.parentIssueId;
          sp2Board.onNewSubIssueCreated(newSubtask);
        });
      }
    }));
  }

  // TODO should be refactored to use the SyncEventType.ISSUE_CREATED event and r emove data.source discrimination
  // TODO should newly created issue from service instead of creating a new one and updating it.
  private initCreatedNewIssueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('CreatedNewIssue').subscribe(data => {
      // Close the issuecard of the new subtask
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(data.tempId);
      if (card) {
        card.discardChangesAndCloseThisCard();
      } else {
        console.log('initCreatedNewIssueSubscription: card not found');
      }
      // MobileApp uses state instead of status
      data.issue.status = data.issue.status ? data.issue.status : data.issue.state;
      const issue: Userstory = new Userstory(null, '', undefined, 0, null,
        null, '', 1, null, data.issue.status, [], [], null, null, null, false, '', '', '', []);

      issue.update(data.issue);
      if (data.source && data.source === 'mobile') {
        sp2Board.onNewIssueCreated(issue);
      } else {
        sp2Board.productBacklog.onNewIssueCreated(issue);
      }
    }));
  }

  private initCreateIssueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('CreateIssue').subscribe(data => {
      const statusTodo: Status = new Status('', '', 10000, 'To Do', new Statuscategory('blue-gray', 2, 'new'));
      const newIssue: Userstory = new Userstory(null, '', undefined, Number(data.id),
        null, null, '', 1, null, statusTodo, [],
        [], null, null, null, false, '', '', '', []);

      sp2Board.onNewIssueCreated(newIssue);
    }));
  }

  private initCreateSubIssueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('CreateSubIssue').subscribe(data => {
      const statusTodo: Status = new Status('', '', 10000, 'To Do', new Statuscategory('blue-gray', 2, 'new'));
      const newIssue: Task = new Task(Number(data.id), '', '', 0, 0, null,
        null, statusTodo, Number(data.parentId), [], [], '', false, [], '', '', '');
      sp2Board.onNewSubIssueCreated(newIssue);
    }));
  }

  private initDiscardCreateIssueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('DiscardCreateIssue').subscribe(data => {
      sp2Board.onIssueRemoved(data.id);
    }));
  }

  private initDiscardCreateSubIssueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('DiscardCreateSubIssue').subscribe(data => {
      sp2Board.onSubIssueRemoved(data.id);
    }));
  }

  private initUpdateIssueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('UpdateIssue').subscribe(data => {
      let updateIssue: Userstory = null;
      sp2Board.issues.forEach((userstory: Userstory) => {
        if (Number(userstory.id) === Number(data.id)) {
          updateIssue = userstory;
        }
      });

      if (updateIssue) {
        const issue = data.issue;
        const issueType: string = IssueTypeMappingUtil.mapToJiraIssueTypeName(issue.issueType);
        issue.issuetype = [];
        issue.issuetype.name = issueType;
        if (data.issue.tasks) {
          issue.tasks = this.userStoryService.mapJSONToTasks(data.issue.tasks, data.id);
        }
        updateIssue.update(issue);
      } else {
        console.log('initUpdateIssueSubscription: issue not found!');
      }
    }));
  }

  private initUpdateSubIssueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('UpdateSubIssue').subscribe(data => {
      let updateIssue: Task = null;
      sp2Board.issues.forEach((userstory: Userstory) => {
        userstory.tasks.forEach((task: Task) => {
          if (Number(task.id) === Number(data.id)) {
            updateIssue = task;
          }
        });
      });

      if (updateIssue) {
        const issue = data.issue;
        const issueType: string = IssueTypeMappingUtil.mapToJiraIssueTypeName(issue.issueType);
        issue.issuetype = [];
        issue.issuetype.name = issueType;
        updateIssue.update(issue);
      } else {
        console.log('initUpdateSubIssueSubscription: issue not found!');
      }
    }));
  }

  private initDeleteIssueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUE_DELETED).subscribe(payload => {
      const event = EventIssueDeleted.fromPayload(payload);
      let issue: IssueComponent = sp2Board.getIssueComponent(event.issueId);
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      // If issue was not found in SP2 data, check product backlog component
      if (!issue) {
        issue = sp2Board.productBacklog.getIssueComponent(event.issueId);
      }
      if (issue && card) {
        issue.destroyIssueCard(card);
        card.closeThisCard();
      } else {
        console.log('initDeleteIssueSubscription: issue or card not found!');
      }
      if (event.isSubissue) {
        sp2Board.onSubIssueRemoved(event.issueId);
      } else {
        sp2Board.onIssueRemoved(event.issueId);
        sp2Board.productBacklog.onIssueRemoved(event.issueId);
      }
    }));
  }

  private initChangeIssueOrderSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('changeIssueOrder').subscribe(data => {
      if (data.component === 'sprint-backlog') {
        moveItemInArray(sp2Board.issues, data.indexFrom, data.indexTo);
      }
    }));
  }

  private initChangeTaskOrderSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('changeTaskOrder').subscribe(data => {
      sp2Board.issues.forEach(issue => {
        if (Number(issue.id) === Number(data.issueId)) {
          moveItemInArray(issue.tasks, data.indexFrom, data.indexTo);
        }
      });
    }));
  }

  private initMoveTaskToOtherIssueSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('moveTaskToOtherIssue').subscribe(data => {
      const projectKey: string = sp2Board.projectKey.toString();
      // Because JIRA does not allow moving subtasks via API, we create a new subtask within new issue and delete the old one
      // Thus, we can not just move the subtask, we need to reload both issues with their subtasks
      sp2Board.issues.forEach(issue => {
        if (Number(issue.id) === Number(data.issueIdFrom)) {
          this.userStoryService.getSubissues(projectKey, data.issueIdFrom).subscribe(tasks => {
            issue.tasks = tasks;
          });
        }

        if (Number(issue.id) === Number(data.issueIdTo)) {
          this.userStoryService.getSubissues(projectKey, data.issueIdTo).subscribe(tasks => {
            issue.tasks = tasks;
          });
        }
      });
    }));
  }

  private initMoveIssueToSprintBacklogSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('moveIssueToSprintBacklog').subscribe(data => {
      const issue: Userstory = new Userstory(null, '', undefined, 0, null,
        null, '', 1, null, data.issue.status, [], [], null, null, null, false, '', '', '', []);

      issue.update(data.issue);
      sp2Board.issues.push(issue);
      moveItemInArray(sp2Board.issues, sp2Board.issues.length - 1, data.indexTo);
    }));
  }

  private initMoveIssueToProductBacklogSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('moveIssueToProductBacklog').subscribe(data => {
      sp2Board.issues.splice(data.indexFrom, 1);
    }));
  }

  private initVisibleColumnsChangeSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('visibleColumnsChange').subscribe(data => {
      const scrollContainer = document.querySelector('#sp2-column-view');
      const currentScrollPercentage = 100 * scrollContainer.scrollLeft / (scrollContainer.scrollWidth - scrollContainer.clientWidth);
      const scrollLeft = (data.scrollPercentage / 100) * (scrollContainer.scrollWidth - scrollContainer.clientWidth);

      // Check if the current rounded scrolling percentage is the same as the scroll event to prevent a sync-loop
      if (Math.floor(currentScrollPercentage) !== Math.floor(data.scrollPercentage)) {
        document.querySelector('#sp2-column-view').scrollLeft = scrollLeft;
      }
    }));
  }

  private initAddSplitIssueAtIndexSubscription(sp2Board: Sprintplanning2ViewComponent) {
    sp2Board.subscriptions.push(this.syncService.subscribeEvent<any>('addSplitIssueAtIndex').subscribe(data => {
      const issue: Userstory = new Userstory(null, '', undefined, 0, null,
        null, '', 1, null, data.issue.status, [], [], null, null, null, false, '', '', '', []);

      issue.update(data.issue);
      if (issue instanceof Userstory) {
        sp2Board.issues.push(issue);
        moveItemInArray(sp2Board.issues, sp2Board.issues.lastIndexOf(issue), data.index);
      }
    }));
  }
}
